import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_ENDPOINTS } from '../constants/api-endpoints';
import { ApiResponse } from '../models/api-response.models';
import { unwrapApiResponse } from '../utils/api-response';
import { ApiService } from './api.service';

export interface AppContentPage {
  id: string;
  slug: string;
  title: string;
  summary: string;
  content: string;
  category: string;
  displayOrder: number;
  isPublished: boolean;
  updatedAt: string;
}

export type SaveAppContentPage = Omit<AppContentPage, 'id' | 'updatedAt'>;
export interface SellerVerificationOffer {
  title: string;
  description: string;
  price: number;
  currency: string;
  isEnabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppContentService {
  private readonly api = inject(ApiService);

  listAll(): Observable<AppContentPage[]> {
    return this.api
      .get<ApiResponse<{ pages: AppContentPage[] }> | { pages: AppContentPage[] }>(
        API_ENDPOINTS.appContent.all,
        undefined,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response).pages ?? []));
  }

  create(page: SaveAppContentPage): Observable<AppContentPage> {
    return this.api
      .post<ApiResponse<{ page: AppContentPage }> | { page: AppContentPage }, SaveAppContentPage>(
        API_ENDPOINTS.appContent.admin,
        page,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response).page));
  }

  update(id: string, page: SaveAppContentPage): Observable<AppContentPage> {
    return this.api
      .put<ApiResponse<{ page: AppContentPage }> | { page: AppContentPage }, SaveAppContentPage>(
        API_ENDPOINTS.appContent.byId(id),
        page,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response).page));
  }

  getVerificationOffer(): Observable<SellerVerificationOffer> {
    return this.api
      .get<ApiResponse<{ offer: SellerVerificationOffer }> | { offer: SellerVerificationOffer }>(
        API_ENDPOINTS.appContent.verificationOffer,
        undefined,
        'marketplace',
      )
      .pipe(map((response) => unwrapApiResponse(response).offer));
  }

  updateVerificationOffer(offer: SellerVerificationOffer): Observable<SellerVerificationOffer> {
    return this.api
      .put<
        ApiResponse<{ offer: SellerVerificationOffer }> | { offer: SellerVerificationOffer },
        SellerVerificationOffer
      >(API_ENDPOINTS.appContent.adminVerificationOffer, offer, 'marketplace')
      .pipe(map((response) => unwrapApiResponse(response).offer));
  }
}
